import { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * createRootElement - Creates a new div and appends it to the document.body
 *
 * @return {Element} DOM element div that was created and appended to body
 */
function createRootElement() {
  const element = document.createElement('div');
  document.body.appendChild(element);
  return element;
}

/**
 * RenderInBodyWithPortal
 *
 * Renders children to root level dom node by appending a new div and
 * creates a portal to the new div. Appended div is removed on unmount
 *
 * This component should be used when needing to pass
 * data using context or provider pattern and render to the top level dom.
 * This is paired excellently with a modal component
 *
 * @param {node} children The children that will be mounted to DOM body
 * @returns {ReactElement} The react element returned from Portal target.
 */
export default function RenderInBodyWithPortal({ children }) {
  const parentRef = useRef();
  if (!parentRef.current) {
    parentRef.current = createRootElement();
  }

  useEffect(() => () => document.body.removeChild(parentRef.current), []);

  return createPortal(children, parentRef.current);
}
